/**
 * A locked checklist, read as the record of the visit: every section, with
 * each item it showed, the answer given with its flag and whether it raised
 * a follow-up action, and the note and photos taken with it. Also what the
 * tenant's rules found in a checklist, and why.
 */

import { useTranslation } from 'react-i18next'
import { ClipboardList, ListPlus, OctagonX, Scale } from 'lucide-react'

import { EditableDetailCard } from '@/admin/components/shared/editable-detail-card'
import type { CardFieldItem } from '@/admin/components/shared/editable-card-types'
import { Badge } from '@/shared/components/ui/badge'
import { cn } from '@/shared/lib/utils'
import {
  defineField,
  formatDisplayValue,
  isFieldVisible,
  normaliseRegistrationField,
} from '@/shared/lib/forms'
import type {
  ChecklistEvaluation,
  ChecklistPhoto,
  ChecklistQuestion,
  InspectionChecklist,
} from '@/admin/hooks/use-inspections'

const PICK_TYPES = new Set(['SELECT', 'MULTI_SELECT'])

export function ChecklistRecord({ checklist }: { checklist: InspectionChecklist }) {
  const { answers, notes, photos } = checklist
  return (
    <div className="flex flex-col gap-4" data-testid="inspection-checklist-record">
      {checklist.sections.map((section) => {
        const items: CardFieldItem[] = checklist.questions
          .filter(
            (question) =>
              question.section === section.key &&
              isFieldVisible(question.configuration_data, answers),
          )
          .map((question) => {
            const code = question.code_name
            const shown = {
              note: notes[code] ?? '',
              photos: photos[code] ?? [],
            }
            const said = shown.note !== '' || shown.photos.length > 0
            return {
              field: normaliseRegistrationField({
                ...question,
                options: question.options as never,
                validation_rules: question.validation_rules as never,
              }),
              value: answers[code],
              displayValue: said || PICK_TYPES.has(question.field_type) ? (
                <Recorded question={question} value={answers[code]} {...shown} />
              ) : undefined,
            }
          })
        return (
          <EditableDetailCard
            key={section.key}
            title={section.title || checklist.template?.name || ''}
            icon={<ClipboardList size={16} />}
            items={items}
            collapsible={false}
            testId={`inspection-checklist-record-${section.key}`}
          />
        )
      })}
    </div>
  )
}

/** Each value the tenant's rules set, with the reasons they gave. */
export function RulesFound({ evaluation }: { evaluation: ChecklistEvaluation }) {
  const { t } = useTranslation()
  const items: CardFieldItem[] = evaluation.found.map((found) => ({
    field: defineField({
      code_name: found.target,
      display_name: found.label,
      field_type: 'TEXT',
    }),
    value: found.values.map((value) => value.label).join(', '),
    displayValue: (
      <span
        className="flex flex-col items-end gap-1.5"
        data-testid={`inspection-checklist-evaluation-${found.target}`}
      >
        {found.values.length > 0 ? (
          <span className="flex flex-wrap justify-end gap-1">
            {found.values.map((value) => (
              <Badge key={String(value.value)} variant="secondary">
                {value.label}
              </Badge>
            ))}
          </span>
        ) : (
          <span className="text-[var(--ink-3)]">-</span>
        )}
        <span className="flex flex-col items-end gap-0.5 text-[12px] text-[var(--ink-3)]">
          {found.reasons.map((reason, index) => (
            <span key={index}>{reason}</span>
          ))}
        </span>
      </span>
    ),
  }))
  return (
    <EditableDetailCard
      title={t('inspections.checklist.rulesFound', 'What the rules found')}
      icon={<Scale size={16} />}
      items={items}
      collapsible={false}
      testId="inspection-checklist-evaluation"
    />
  )
}

function Recorded({
  question,
  value,
  note,
  photos,
}: {
  question: ChecklistQuestion
  value: unknown
  note: string
  photos: ChecklistPhoto[]
}) {
  const picked = (Array.isArray(value) ? value : [value]).filter((v) => v != null && v !== '')
  const answer = PICK_TYPES.has(question.field_type)
    ? picked.map((choice) => (
        <AnswerBadge
          key={String(choice)}
          option={
            question.options.find((option) => option.value === choice) ?? {
              label: String(choice),
            }
          }
        />
      ))
    : formatDisplayValue(
        normaliseRegistrationField({
          ...question,
          options: question.options as never,
          validation_rules: question.validation_rules as never,
        }),
        value,
      )
  return (
    <span className="flex flex-col items-end gap-1.5">
      {picked.length > 0 ? (
        <span className="flex flex-wrap justify-end gap-1">{answer}</span>
      ) : (
        <span className="text-[var(--ink-3)]">-</span>
      )}
      {note ? (
        <span className="whitespace-pre-line text-[13px] text-[var(--ink-2)]">{note}</span>
      ) : null}
      {photos.length > 0 ? (
        <span className="flex flex-wrap justify-end gap-1.5">
          {photos.map((photo) => (
            <a
              key={photo.uuid}
              href={photo.file_url ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={photo.file_url ?? undefined}
                alt={photo.file_name}
                className="size-14 rounded-md border border-[var(--rule)] object-cover"
              />
            </a>
          ))}
        </span>
      ) : null}
    </span>
  )
}

/** A picked answer as it read on site: its flag and whether it raised an action. */
function AnswerBadge({ option }: { option: Record<string, unknown> }) {
  const { t } = useTranslation()
  const flag = option.severity === 'red' || option.severity === 'yellow' ? option.severity : null
  return (
    <Badge
      variant={flag === 'red' ? 'destructive' : flag === 'yellow' ? 'outline' : 'secondary'}
      className={cn(
        'gap-1',
        flag === 'yellow' &&
          'border-[var(--status-warn)] bg-[var(--status-warn-bg)] text-[var(--status-warn)]',
      )}
    >
      {String(option.label ?? '')}
      {option.creates_action ? <ListPlus aria-hidden className="size-3" /> : null}
      {option.blocks ? <OctagonX aria-hidden className="size-3" /> : null}
      {flag ? (
        <span className="sr-only">
          {flag === 'red'
            ? t('forms.settings.flagRed', 'Red flag')
            : t('forms.settings.flagAmber', 'Amber flag')}
        </span>
      ) : null}
      {option.creates_action ? (
        <span className="sr-only">
          {t('forms.settings.createsAction', 'Creates a follow-up action')}
        </span>
      ) : null}
      {option.blocks ? (
        <span className="sr-only">{t('forms.settings.blocks', 'Stops the work')}</span>
      ) : null}
    </Badge>
  )
}
